import React, { Component } from 'react';
import { CalendarXIcon, BatteryLowIcon, BrainIcon, AwardIcon, ArrowRightIcon } from 'lucide-react';
type Point = {
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  trigger: string;
  body: string;
};
const points: Point[] = [{
  icon: CalendarXIcon,
  trigger: 'La domenica sera finisci a preparare attività artistiche invece di riposarti.',
  body: 'Sacrifichi i weekend per cercare idee su Pinterest invece di vivere la tua famiglia.'
}, {
  icon: BatteryLowIcon,
  trigger: 'Cerchi continuamente idee nuove per non fare sempre "i soliti lavoretti".',
  body: 'Vuoi lezioni artistiche moderne, creative e davvero coinvolgenti per la tua classe.'
}, {
  icon: BrainIcon,
  trigger: 'Hai bisogno di materiali già pronti anche per BES e DSA.',
  body: 'Cerchi versioni facilitate e strumenti inclusivi per far partecipare tutti senza stress.'
}, {
  icon: AwardIcon,
  trigger: 'Vuoi essere vista come la maestra creativa che realizza progetti incredibili.',
  body: 'Quella che presenta a Dirigente e genitori una programmazione artistica annuale finalmente organizzata.'
}];
export function ForWhomSection() {
  return <section className="w-full bg-bg-dark py-16 sm:py-24 px-4 relative overflow-hidden" data-id="element-251">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none opacity-60" style={{
      background: 'radial-gradient(circle, rgba(234,88,12,0.08) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-252" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none opacity-50" style={{
      background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)'
    }} aria-hidden="true" data-id="element-253" />

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-254">
        {/* Section header */}
        <div className="text-center mb-10 sm:mb-14" data-id="element-255">
          <span className="inline-flex items-center gap-2 text-primary font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] bg-primary/10 border border-primary/20 px-4 py-1.5 rounded-full mb-4" data-id="element-256">
            👀 Riconosci queste scene?
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main tracking-tight leading-[1.1] mb-4" data-id="element-257">
            ARTE PRONTA è per te
            <br className="hidden sm:block" data-id="element-258" />
            <span className="text-primary" data-id="element-259">
              {' '}
              se almeno una di queste è la tua realtà.
            </span>
          </h2>
          <p className="text-text-sec text-base sm:text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-260">
            Non è per tutte. È per le maestre che vogliono{' '}
            <span className="text-text-main font-bold" data-id="element-261">smettere</span> di vivere
            così.
          </p>
        </div>

        {/* Points grid — 2 cols on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4" data-id="element-262">
          {points.map((point, i) => {
          const Icon = point.icon;
          return <div key={i} className="relative flex items-start gap-4 bg-bg-alt rounded-2xl p-5 sm:p-6 border border-border-light hover:border-primary/60 transition-colors group overflow-hidden" data-id="element-263">
                {/* Number badge */}
                <span className="absolute top-3 right-4 text-text-sec/30 font-black text-3xl sm:text-4xl leading-none tracking-tighter pointer-events-none" data-id="element-264">
                  0{i + 1}
                </span>

                {/* Icon */}
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 group-hover:border-primary/60 transition-colors" data-id="element-265">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={2.25} data-id="element-266" />
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1 pr-8" data-id="element-267">
                  <p className="text-text-main font-black text-base sm:text-lg leading-tight mb-1.5" data-id="element-268">
                    {point.trigger}
                  </p>
                  <p className="text-text-sec text-sm sm:text-base leading-relaxed" data-id="element-269">
                    {point.body}
                  </p>
                </div>
              </div>;
        })}
        </div>

        {/* Bottom self-qualification CTA */}
        <div className="mt-8 sm:mt-12 bg-bg-alt border-2 border-primary/40 rounded-2xl sm:rounded-3xl p-5 sm:p-8 relative overflow-hidden shadow-[0_0_40px_rgba(234,88,12,0.12)]" data-id="element-270">
          <div className="absolute inset-0 pointer-events-none opacity-50" style={{
          background: 'radial-gradient(ellipse at center, rgba(234,88,12,0.10) 0%, transparent 70%)'
        }} aria-hidden="true" data-id="element-271" />
          <div className="relative z-10 flex flex-col sm:flex-row items-center justify-between gap-5 sm:gap-6" data-id="element-272">
            <div className="text-center sm:text-left flex-1" data-id="element-273">
              <p className="text-text-sec text-[11px] sm:text-xs font-black uppercase tracking-[0.15em] mb-2" data-id="element-274">
                ✋ Ti sei riconosciuta?
              </p>
              <p className="text-text-main font-black text-lg sm:text-2xl leading-tight" data-id="element-275">
                Allora{' '}
                <span className="text-primary" data-id="element-276">
                  questo Kit è esattamente per te.
                </span>
              </p>
              <p className="text-text-sec text-sm sm:text-base mt-1.5 leading-snug" data-id="element-277">
                Bastano 5 minuti per preparare una lezione artistica completa.
              </p>
            </div>
            <a href="#pricing" style={{
            boxShadow: '0 0 20px rgba(57,255,20,0.55), 0 8px 24px rgba(57,255,20,0.35)'
          }} className="inline-flex items-center justify-center gap-2 font-black text-sm sm:text-base px-6 py-3.5 sm:px-7 sm:py-4 rounded-xl border-2 border-white/30 whitespace-nowrap uppercase tracking-tight transition-transform hover:scale-[1.03] active:scale-[0.98] text-[#FFFFFF] bg-[#22C55E]" data-id="element-278">
              Voglio il Kit
              <ArrowRightIcon className="w-4 h-4" strokeWidth={3} data-id="element-279" />
            </a>
          </div>
        </div>
      </div>
    </section>;
}